import axios from 'axios';

export interface ApiResponse<T> {
  data: T[];
  count?: number;
  next?: string;
  previous?: string;
}

export interface ParsedResponse<T> {
  data: T[];
  totalCount: number;
  hasNext: boolean;
  hasPrevious: boolean;
}

/**
 * Parses API responses that may come in different formats:
 * - Direct array
 * - Paginated object with data array
 * - Nested object with various array keys
 */
export function parseApiResponse<T>(
  response: any,
  arrayKeys: string[] = ['data', 'results', 'items']
): ParsedResponse<T> {
  const responseData = response?.data || response;

  // Handle direct array responses
  if (Array.isArray(responseData)) {
    return {
      data: responseData,
      totalCount: responseData.length,
      hasNext: false,
      hasPrevious: false,
    };
  }

  // Handle paginated responses
  if (responseData && typeof responseData === 'object') {
    // Check for standard pagination fields
    if ('results' in responseData && Array.isArray(responseData.results)) {
      return {
        data: responseData.results,
        totalCount: responseData.count || responseData.results.length,
        hasNext: !!responseData.next,
        hasPrevious: !!responseData.previous,
      };
    }

    // Check for data field
    if ('data' in responseData && Array.isArray(responseData.data)) {
      return {
        data: responseData.data,
        totalCount: responseData.count || responseData.data.length,
        hasNext: !!responseData.next,
        hasPrevious: !!responseData.previous,
      };
    }

    // Check for other common array keys
    for (const key of arrayKeys) {
      if (key in responseData && Array.isArray(responseData[key])) {
        return {
          data: responseData[key],
          totalCount: responseData.count || responseData[key].length,
          hasNext: !!responseData.next,
          hasPrevious: !!responseData.previous,
        };
      }
    }

    // Check if the object itself contains array-like properties
    const keys = Object.keys(responseData);
    for (const key of keys) {
      if (Array.isArray(responseData[key])) {
        return {
          data: responseData[key],
          totalCount: responseData.count || responseData[key].length,
          hasNext: !!responseData.next,
          hasPrevious: !!responseData.previous,
        };
      }
    }
  }

  // Fallback: return empty array
  console.warn('Unable to parse API response, returning empty array:', responseData);
  return {
    data: [],
    totalCount: 0,
    hasNext: false,
    hasPrevious: false,
  };
}

/**
 * Extracts just the data array from API responses
 */
export function extractDataArray<T>(
  response: any,
  arrayKeys: string[] = ['data', 'results', 'items']
): T[] {
  const parsed = parseApiResponse<T>(response, arrayKeys);
  return parsed.data;
}

/**
 * Handles API response with filtering and pagination
 */
export function handleFilteredResponse<T>(
  response: any,
  filters?: Record<string, any>
): T[] {
  const data = extractDataArray<T>(response);
  
  if (!filters) {
    return data;
  }

  return data.filter(item => {
    return Object.entries(filters).every(([key, value]) => {
      if (value === null || value === undefined) return true;
      
      const itemValue = (item as any)[key];
      if (typeof value === 'string') {
        return itemValue?.toString().toLowerCase().includes(value.toLowerCase());
      }
      return itemValue === value;
    });
  });
}

/**
 * Normalizes API response structure for consistent handling
 */
export function normalizeResponse<T>(response: any): {
  data: T[];
  meta: {
    count: number;
    hasNext: boolean;
    hasPrevious: boolean;
    page?: number;
    pageSize?: number;
  };
} {
  const parsed = parseApiResponse<T>(response);
  
  return {
    data: parsed.data,
    meta: {
      count: parsed.totalCount,
      hasNext: parsed.hasNext,
      hasPrevious: parsed.hasPrevious,
    },
  };
}

/**
 * Type guard to check if response is an error
 */
export function isErrorResponse(response: any): response is { error: string } {
  return response && typeof response === 'object' && 'error' in response;
}

/**
 * Utility to check if a response is empty
 */
export function isEmptyResponse(response: any): boolean {
  const data = extractDataArray(response);
  return data.length === 0;
}